// `/debug` › Push — send ANY person a test notification.
//
// The self-send in Settings can only reach the caller's own phone, which cannot answer the question that actually
// comes up: "did the tour manager's phone get it?". This finds anybody, shows every device they have registered
// (disabled ones included — a token soft-disabled by a 410 is the commonest reason a phone goes quiet), and sends.
//
// Two delivery paths, because they fail differently. DIRECT sends inline from the API and reports each device's
// APNs outcome on the spot. VIA WORKER enqueues the real `ac.cmd.notify.push` command, so the outbox → JetStream →
// PushSendWorker leg (badge, category, chat mutes) runs as production runs it; its result is in the worker log.
import { useState } from "react";
import { Button, Card, Field, SegmentedControl, Tag, Textarea } from "@cntrl/ui";
import { errorMessage } from "../../app/hooks/apiError.js";
import { useMe } from "../../app/hooks/useSession.js";
import {
  usePushDebugDevices,
  usePushDebugPeople,
  usePushDebugSend,
  type PushDebugDeviceResult,
  type PushDebugPerson,
} from "../../app/hooks/usePushDebug.js";
import styles from "./DebugScreen.module.scss";
import own from "./PushPanel.module.scss";

type Delivery = "direct" | "worker";

const stamp = (iso: string | null | undefined) => (iso ? new Date(iso).toLocaleString() : "—");

const OUTCOME_TONE: Record<string, "success" | "warning" | "danger" | "neutral"> = {
  Sent: "success",
  Unregistered: "danger",
  TransientFailure: "warning",
  PermanentFailure: "danger",
  ProviderUnconfigured: "neutral",
};

function RecipientSearch({ onPick }: { onPick: (p: PushDebugPerson) => void }) {
  const [query, setQuery] = useState("");
  const people = usePushDebugPeople(query);
  const me = useMe();
  const person = me.data?.person;
  const self: PushDebugPerson | null = person?.personId
    ? {
        personId: person.personId,
        name: person.displayName || `${person.firstName ?? ""} ${person.lastName ?? ""}`.trim() || (person.email ?? ""),
        email: person.email ?? "",
        status: "active",
      }
    : null;

  return (
    <div className={styles.stack}>
      <div className={own.searchRow}>
        <div className={own.search}>
          <Field
            label="Find a person"
            type="search"
            placeholder="Name or email"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
        </div>
        {self && (
          <Button variant="ghost" size="sm" onClick={() => onPick(self)}>
            Me
          </Button>
        )}
      </div>

      {query.trim().length >= 2 && (
        <>
          {people.isLoading && <p className={styles.hint}>Searching…</p>}
          {people.isError && <p className={styles.error}>{errorMessage(people.error, "Search failed.")}</p>}
          {people.data && people.data.length === 0 && <p className={styles.hint}>Nobody matches.</p>}
          {people.data && people.data.length > 0 && (
            <ul className={own.results}>
              {people.data.map((p) => (
                <li key={p.personId}>
                  <button type="button" className={own.result} onClick={() => onPick(p)}>
                    <strong>{p.name}</strong>
                    <span className={own.email}>{p.email}</span>
                    {p.status !== "active" && (
                      <Tag tone="warning" size="sm">
                        {p.status}
                      </Tag>
                    )}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </>
      )}
    </div>
  );
}

function Devices({ personId }: { personId: string }) {
  const devices = usePushDebugDevices(personId);

  if (devices.isLoading) return <p className={styles.hint}>Reading devices…</p>;
  if (devices.isError) return <p className={styles.error}>{errorMessage(devices.error, "Could not read devices.")}</p>;
  const doc = devices.data;
  if (!doc) return null;

  return (
    <div className={styles.stack}>
      {!doc.providerConfigured && (
        <p className={styles.notice}>
          APNs is not configured on this API host (no <code>.p8</code>), so a DIRECT send is a no-op for every device.
        </p>
      )}
      {doc.devices.length === 0 ? (
        <p className={styles.error}>
          {doc.person.name} has no registered devices. A device registers when the iOS app is opened signed in, with
          notifications allowed.
        </p>
      ) : (
        <div className={styles.tableWrap}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th>Token</th>
                <th>Platform</th>
                <th>Host</th>
                <th>Device</th>
                <th>App</th>
                <th>Last seen</th>
                <th>State</th>
              </tr>
            </thead>
            <tbody>
              {doc.devices.map((d) => (
                <tr key={d.pushTokenId}>
                  <td>
                    <code>{d.tokenTail}</code>
                  </td>
                  <td>{d.platform}</td>
                  <td>{d.appEnv}</td>
                  <td>{d.deviceModel ?? "—"}</td>
                  <td>{d.appVersion ?? "—"}</td>
                  <td>{stamp(d.lastSeenOn ?? d.createdOn)}</td>
                  <td>
                    <Tag tone={d.enabled ? "success" : "neutral"} size="sm">
                      {d.enabled ? "enabled" : "disabled"}
                    </Tag>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function DeviceResults({ rows }: { rows: PushDebugDeviceResult[] }) {
  return (
    <div className={styles.tableWrap}>
      <table className={styles.table}>
        <thead>
          <tr>
            <th>Device</th>
            <th>Outcome</th>
            <th>Reason</th>
            <th>Host used</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.pushTokenId}>
              <td>
                <code>{r.tokenTail}</code>
              </td>
              <td>
                <Tag tone={OUTCOME_TONE[r.outcome] ?? "neutral"} size="sm">
                  {r.outcome}
                </Tag>
              </td>
              <td>{r.reasonCode ?? "—"}</td>
              <td>{r.environmentUsed}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function PushPanel() {
  const [target, setTarget] = useState<PushDebugPerson | null>(null);
  const [title, setTitle] = useState("CNTRL");
  const [body, setBody] = useState("Test push from /debug.");
  const [kind, setKind] = useState("generic");
  const [route, setRoute] = useState("");
  const [delivery, setDelivery] = useState<Delivery>("direct");
  const send = usePushDebugSend();

  const pick = (p: PushDebugPerson) => {
    setTarget(p);
    send.reset();
  };

  const submit = () => {
    if (!target) return;
    send.mutate({ personId: target.personId, title, body, kind, route, viaWorker: delivery === "worker" });
  };

  const result = send.data;

  return (
    <>
      <Card title="Recipient" subtitle="Anybody on this lane — search by name or email">
        <div className={styles.stack}>
          <RecipientSearch onPick={pick} />
          {target && (
            <div className={own.target}>
              <span>
                Sending to <strong>{target.name}</strong> <span className={own.email}>{target.email}</span>
              </span>
              <Button variant="ghost" size="xs" onClick={() => setTarget(null)}>
                Clear
              </Button>
            </div>
          )}
        </div>
      </Card>

      {target && (
        <Card title="Devices" subtitle={`Every push token ${target.name} has registered`}>
          <Devices personId={target.personId} />
        </Card>
      )}

      {target && (
        <Card title="Compose">
          <div className={styles.stack}>
            <Field label="Title" value={title} maxLength={120} onChange={(e) => setTitle(e.target.value)} />
            <Textarea label="Body" value={body} maxLength={500} rows={3} onChange={(e) => setBody(e.target.value)} />
            <div className={own.pair}>
              <Field
                label="Kind"
                value={kind}
                onChange={(e) => setKind(e.target.value)}
                hint="Becomes thread-id and, for a catalogue kind, the iOS category."
              />
              <Field
                label="Tap route"
                placeholder="/booking"
                value={route}
                onChange={(e) => setRoute(e.target.value)}
                hint="Optional app path opened on tap."
              />
            </div>
            <div className={own.sendRow}>
              <SegmentedControl
                label="Delivery"
                value={delivery}
                onChange={setDelivery}
                options={[
                  { value: "direct", label: "Direct" },
                  { value: "worker", label: "Via worker" },
                ]}
              />
              <Button variant="primary" size="sm" onClick={submit} disabled={send.isPending}>
                {send.isPending ? "Sending…" : "Send test push"}
              </Button>
            </div>
            <p className={styles.hint}>
              <strong>Direct</strong> sends from the API now and reports each device&apos;s APNs answer.{" "}
              <strong>Via worker</strong> enqueues the real <code>ac.cmd.notify.push</code> command, so it also
              exercises the outbox, the worker, the icon badge and chat mutes — its result is in the worker log.
            </p>
          </div>
        </Card>
      )}

      {send.isError && (
        <Card title="Send failed">
          <p className={styles.error}>{errorMessage(send.error, "The send failed.")}</p>
        </Card>
      )}

      {result && (
        <Card title="Result" subtitle={result.viaWorker ? "Via worker" : "Direct"}>
          <div className={styles.stack}>
            <p className={styles.notice}>{result.detail}</p>
            {!result.viaWorker && result.attempted > 0 && (
              <>
                <p className={styles.keys}>
                  attempted {result.attempted} · sent {result.sent} · disabled {result.disabled} · failed{" "}
                  {result.failed} · transient {result.transient}
                </p>
                <DeviceResults rows={result.devices} />
              </>
            )}
          </div>
        </Card>
      )}
    </>
  );
}
